if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        if (!this.constructor.cacheSubscription) {
          this.constructor.cacheSubscription = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
            this.constructor.cache.clear();
            this.constructor.pending.clear();
            this.constructor.cacheVersion += 1;
          });
        }
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      static cache = new Map();
      static sections = new Map();
      static pending = new Map();
      static cacheVersion = 0;
      static cacheLifetime = 30000;

      static async loadProduct(productUrl) {
        const url = new URL(productUrl, window.location.origin);
        const key = url.href;
        const cached = this.cache.get(key);
        if (cached && Date.now() - cached.time < this.cacheLifetime) return cached.html;
        if (this.pending.has(key)) return this.pending.get(key);
        const version = this.cacheVersion;
        const request = (async () => {
          const section = this.sections.get(url.pathname);
          const requestUrl = new URL(url);
          if (section) requestUrl.searchParams.set('section_id', section);
          let response = await fetch(requestUrl.href);
          let html = response.ok ? await response.text() : '';
          let product = new DOMParser().parseFromString(html, 'text/html').querySelector('product-info');
          if (!product && section) {
            this.sections.delete(url.pathname);
            response = await fetch(url.href);
            html = response.ok ? await response.text() : '';
            product = new DOMParser().parseFromString(html, 'text/html').querySelector('product-info');
          }
          if (!product) throw new Error('Quick add product unavailable');
          if (product.dataset.section) this.sections.set(url.pathname, product.dataset.section);
          const content = product.outerHTML;
          if (version === this.cacheVersion) {
            this.cache.delete(key);
            this.cache.set(key, { html: content, time: Date.now() });
            if (this.cache.size > 12) this.cache.delete(this.cache.keys().next().value);
          }
          return content;
        })();
        this.pending.set(key, request);
        try {
          return await request;
        } finally {
          if (this.pending.get(key) === request) this.pending.delete(key);
        }
      }

      hide(preventFocus = false) {
        this.requestId = (this.requestId || 0) + 1;
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';
        this.modalContent.removeAttribute('aria-busy');
        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      async show(opener) {
        const requestId = this.requestId = (this.requestId || 0) + 1;
        const productUrl = opener.getAttribute('data-product-url');
        const status = document.createElement('div');
        status.className = 'quick-add-status';
        status.setAttribute('role', 'status');
        const spinner = opener.querySelector('.loading__spinner')?.cloneNode(true);
        if (spinner) {
          spinner.classList.remove('hidden');
          spinner.removeAttribute('id');
          spinner.setAttribute('aria-hidden', 'true');
          status.appendChild(spinner);
        }
        const label = document.createElement('p');
        label.textContent = this.dataset.loadingText || 'Loading...';
        status.appendChild(label);
        this.modalContent.replaceChildren(status);
        this.modalContent.setAttribute('aria-busy', 'true');
        super.show(opener);

        try {
          const html = await this.constructor.loadProduct(productUrl);
          if (requestId !== this.requestId || !this.hasAttribute('open')) return;
          const productElement = new DOMParser().parseFromString(html, 'text/html').querySelector('product-info');
          this.preprocessHTML(productElement);
          HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
          if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
          if (window.ProductModel) window.ProductModel.loadShopifyXR();
          this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();
          const focusTarget = this.contains(document.activeElement) ? document.activeElement : this.querySelector('[role="dialog"]');
          trapFocus(this, focusTarget);
        } catch (error) {
          if (requestId !== this.requestId || !this.hasAttribute('open')) return;
          const message = document.createElement('p');
          message.setAttribute('role', 'alert');
          message.textContent = this.dataset.errorText || 'Unable to load options. Please open the product page.';
          const link = document.createElement('a');
          link.href = productUrl;
          link.className = 'underlined-link';
          link.textContent = this.dataset.linkText || 'View product';
          status.replaceChildren(message, link);
          this.modalContent.replaceChildren(status);
          trapFocus(this, this.querySelector('[role="dialog"]'));
        } finally {
          if (requestId === this.requestId) this.modalContent.removeAttribute('aria-busy');
        }
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
        if (this.dataset.cardCut) {
          productElement.querySelectorAll('.product__title h1, .product__title h2').forEach(title => {
            title.textContent = `${title.textContent.trim()} - ${this.dataset.cardCut}`;
          });
        }
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        productElement.querySelectorAll('[data-primary-product-gallery]').forEach((gallery) => {
          gallery.removeAttribute('data-primary-product-gallery');
        });

        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
